import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  Package,
  Users,
  ShoppingBag,
  Truck,
  Bell,
  LayoutDashboard,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  UserCog,
  Building2,
  DollarSign,
  Video,
  CreditCard,
  Image
} from 'lucide-react'

export function AdminDashboard() {
  const [stats, setStats] = useState({
    totalProducts: 0,
    pendingProducts: 0,
    totalSellers: 0,
    pendingSellers: 0,
    totalDeliveries: 0,
    pendingDeliveries: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalUsers: 0,
    totalReports: 0,
    pendingVideos: 0,
    pendingLoans: 0,
    activeAds: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      setLoading(true)

      // Fetch products stats
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('status')

      if (productsError) throw productsError

      const totalProducts = products?.length || 0
      const pendingProducts = products?.filter(p => p.status === 'pending').length || 0

      // Fetch sellers stats
      const { data: sellers, error: sellersError } = await supabase
        .from('seller_stores')
        .select('status')

      if (sellersError) throw sellersError

      const totalSellers = sellers?.length || 0
      const pendingSellers = sellers?.filter(s => s.status === 'pending').length || 0

      // Fetch deliveries stats
      const { data: deliveries, error: deliveriesError } = await supabase
        .from('delivery_partners')
        .select('status')

      if (deliveriesError) throw deliveriesError

      const totalDeliveries = deliveries?.length || 0
      const pendingDeliveries = deliveries?.filter(d => d.status === 'pending').length || 0

      // Fetch orders stats
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('final_price')

      if (ordersError) throw ordersError

      const totalOrders = orders?.length || 0
      const totalRevenue = orders?.reduce((sum, order) => sum + (order.final_price || 0), 0) || 0

      // Fetch users stats
      const { count: totalUsers, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact' })

      if (usersError) throw usersError

      // Fetch reports stats
      const { count: totalReports, error: reportsError } = await supabase
        .from('notifications')
        .select('*', { count: 'exact' })
        .eq('type', 'report')

      if (reportsError) throw reportsError

      // Fetch pending videos count
      const { count: pendingVideos, error: videosError } = await supabase
        .from('videos')
        .select('*', { count: 'exact' })
        .eq('status', 'pending')

      if (videosError) throw videosError

      // Fetch pending loans count
      const { count: pendingLoans, error: loansError } = await supabase
        .from('loan_requests')
        .select('*', { count: 'exact' })
        .eq('status', 'pending')

      if (loansError) throw loansError

      // Fetch active advertisements count
      const { count: activeAds, error: adsError } = await supabase
        .from('advertisements')
        .select('*', { count: 'exact' })
        .eq('is_active', true)
        .lte('start_date', new Date().toISOString())
        .gte('end_date', new Date().toISOString())

      if (adsError) throw adsError

      setStats({
        totalProducts,
        pendingProducts,
        totalSellers,
        pendingSellers,
        totalDeliveries,
        pendingDeliveries,
        totalOrders,
        totalRevenue,
        totalUsers: totalUsers || 0,
        totalReports: totalReports || 0,
        pendingVideos: pendingVideos || 0,
        pendingLoans: pendingLoans || 0,
        activeAds: activeAds || 0
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">
          Tableau de Bord Administrateur
        </h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Link
            to="/admin/products"
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Produits
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.totalProducts}
                      </div>
                      {stats.pendingProducts > 0 && (
                        <div className="ml-2 flex items-baseline text-sm font-semibold text-yellow-600">
                          <Clock className="h-4 w-4 mr-1" />
                          {stats.pendingProducts} en attente
                        </div>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/sellers"
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Vendeurs
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.totalSellers}
                      </div>
                      {stats.pendingSellers > 0 && (
                        <div className="ml-2 flex items-baseline text-sm font-semibold text-yellow-600">
                          <Clock className="h-4 w-4 mr-1" />
                          {stats.pendingSellers} en attente
                        </div>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/delivery-partners"
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Truck className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Livreurs
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.totalDeliveries}
                      </div>
                      {stats.pendingDeliveries > 0 && (
                        <div className="ml-2 flex items-baseline text-sm font-semibold text-yellow-600">
                          <Clock className="h-4 w-4 mr-1" />
                          {stats.pendingDeliveries} en attente
                        </div>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/videos"
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Video className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Vidéos
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.pendingVideos}
                      </div>
                      {stats.pendingVideos > 0 && (
                        <div className="ml-2 flex items-baseline text-sm font-semibold text-yellow-600">
                          <Clock className="h-4 w-4 mr-1" />
                          en attente
                        </div>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/loans"
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CreditCard className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Demandes de Prêt
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.pendingLoans}
                      </div>
                      {stats.pendingLoans > 0 && (
                        <div className="ml-2 flex items-baseline text-sm font-semibold text-yellow-600">
                          <Clock className="h-4 w-4 mr-1" />
                          en attente
                        </div>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/advertisements"
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Image className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Publicités
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.activeAds}
                      </div>
                      <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        actives
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Actions Rapides
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/admin/products"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <Package className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Gérer les Produits
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Approuver ou rejeter les nouveaux produits, gérer le catalogue
                </p>
              </div>
            </Link>

            <Link
              to="/admin/videos"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <Video className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Gérer les Vidéos
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Approuver ou rejeter les vidéos soumises par les utilisateurs
                </p>
              </div>
            </Link>

            <Link
              to="/admin/advertisements"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <Image className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Gérer les Publicités
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Créer et gérer les publicités qui s'affichent sur la plateforme
                </p>
              </div>
            </Link>

            <Link
              to="/admin/sellers"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <Users className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Gérer les Vendeurs
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Valider les inscriptions, gérer les comptes vendeurs
                </p>
              </div>
            </Link>

            <Link
              to="/admin/delivery-partners"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <Truck className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Gérer les Livreurs
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Valider les inscriptions, gérer les livreurs
                </p>
              </div>
            </Link>

            <Link
              to="/admin/manager-dept"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <Building2 className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Départements
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Gérer les managers et les départements
                </p>
              </div>
            </Link>

            <Link
              to="/admin/loans"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <CreditCard className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Demandes de Prêt
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Examiner et répondre aux demandes de financement des vendeurs
                </p>
              </div>
            </Link>

            <Link
              to="/admin/users"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <UserCog className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Utilisateurs
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Gérer les comptes utilisateurs et les rôles
                </p>
              </div>
            </Link>

            <Link
              to="/admin/payments"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-primary/10 ring-4 ring-white">
                  <DollarSign className="h-6 w-6 text-primary" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Paiements
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Gérer les méthodes de paiement des vendeurs
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}